﻿import {
  ArrowDownRight,
  ArrowUpRight,
  CreditCard,
  DollarSign,
  Layers,
  Percent,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useFinancialHistory } from "@/hooks/growth/useFinancialHistory";
import { getPaymentMethodColor, getPaymentMethodIcon } from "./utils";

export function FinancialHistoryPanel({
  activeCompanyId,
}: {
  activeCompanyId: string | null;
}) {
  const {
    period,
    setPeriod,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    metrics,
    isLoading,
  } = useFinancialHistory(activeCompanyId);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm mb-6 space-y-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 border-b border-slate-800/80 pb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 text-blue-500 rounded-xl border border-blue-500/20 shadow-sm">
            <Wallet size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
              Histórico Financeiro
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Painel Completo
              </span>
            </h2>
            <p className="text-sm text-slate-400">
              Receitas, despesas, margens, meios de pagamento e serviços mais
              rentáveis.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-slate-950 p-1.5 rounded-xl border border-slate-800 w-full lg:w-auto">
          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPeriod("7d")}
              className={`flex-1 sm:flex-initial transition-all duration-200 ease-out font-medium ${
                period === "7d"
                  ? "bg-blue-600 text-white shadow-sm hover:bg-blue-500"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
              }`}
            >
              7 dias
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPeriod("30d")}
              className={`flex-1 sm:flex-initial transition-all duration-200 ease-out font-medium ${
                period === "30d"
                  ? "bg-blue-600 text-white shadow-sm hover:bg-blue-500"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
              }`}
            >
              30 dias
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPeriod("custom")}
              className={`flex-1 sm:flex-initial transition-all duration-200 ease-out font-medium ${
                period === "custom"
                  ? "bg-blue-600 text-white shadow-sm hover:bg-blue-500"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
              }`}
            >
              Período Específico
            </Button>
          </div>

          {period === "custom" && (
            <div className="flex items-center gap-2 border-t sm:border-t-0 sm:border-l border-slate-800 pt-2 sm:pt-0 sm:pl-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <span>De:</span>
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-xs h-8 w-32 text-slate-200 transition-all duration-200 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <span>Até:</span>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-xs h-8 w-32 text-slate-200 transition-all duration-200 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={`kpi-skel-${i}`}
                className="h-32 rounded-xl bg-slate-800/60"
              />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-80 rounded-xl bg-slate-800/60" />
            <Skeleton className="h-80 rounded-xl bg-slate-800/60" />
          </div>
          <Skeleton className="h-28 rounded-xl bg-slate-800/60" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-950 border border-slate-800/80 hover:border-slate-700/80 p-5 rounded-xl shadow-sm transition-all duration-200 ease-out flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-400">
                  Receitas (Entradas)
                </span>
                <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <ArrowUpRight size={20} className="stroke-[2.5]" />
                </div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-extrabold text-emerald-400 tracking-tight">
                  R${" "}
                  {metrics.receitas.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h3>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <TrendingUp size={13} className="text-emerald-500" />
                  Entradas brutas no período
                </p>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800/80 hover:border-slate-700/80 p-5 rounded-xl shadow-sm transition-all duration-200 ease-out flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-400">
                  Despesas (Saídas)
                </span>
                <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20">
                  <ArrowDownRight size={20} className="stroke-[2.5]" />
                </div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-extrabold text-rose-400 tracking-tight">
                  R${" "}
                  {metrics.despesas.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h3>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <TrendingDown size={13} className="text-rose-500" />
                  Custos e saídas de caixa
                </p>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800/80 hover:border-slate-700/80 p-5 rounded-xl shadow-sm transition-all duration-200 ease-out flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-400">
                  Lucro Presumido
                </span>
                <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  <DollarSign size={20} className="stroke-[2.5]" />
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <h3
                    className={`text-2xl font-extrabold tracking-tight ${
                      metrics.lucro >= 0 ? "text-blue-400" : "text-rose-400"
                    }`}
                  >
                    R${" "}
                    {metrics.lucro.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                      metrics.lucroMargin >= 0
                        ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                        : "bg-rose-500/10 text-rose-400 border-rose-500/20"
                    }`}
                  >
                    Margem: {metrics.lucroMargin.toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Saldo líquido operacional
                </p>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800/80 hover:border-slate-700/80 p-5 rounded-xl shadow-sm transition-all duration-200 ease-out flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-400">
                  Ticket Médio
                </span>
                <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  <Percent size={20} className="stroke-[2.5]" />
                </div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-extrabold text-purple-400 tracking-tight">
                  R${" "}
                  {metrics.ticketMedio.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {metrics.completedOrdersCount}{" "}
                  {metrics.completedOrdersCount === 1
                    ? "veículo finalizado"
                    : "veículos finalizados"}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-950 border border-slate-800/80 rounded-xl p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800/60 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-500/10 text-blue-400 rounded-lg">
                    <CreditCard size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-100">
                      Mix de Meios de Pagamento
                    </h3>
                    <p className="text-xs text-slate-400">
                      Distribuição do volume financeiro por método
                    </p>
                  </div>
                </div>
              </div>

              {metrics.paymentMethodsList.every((m) => m.amount === 0) ? (
                <div className="p-8 text-center border border-slate-800/60 border-dashed rounded-xl">
                  <CreditCard
                    className="mx-auto text-slate-600 mb-2"
                    size={32}
                  />
                  <p className="text-sm font-medium text-slate-400">
                    Nenhum pagamento registrado
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Nenhum valor recebido no período selecionado.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {metrics.paymentMethodsList.map((method) => {
                    const styling = getPaymentMethodColor(method.name);
                    return (
                      <div key={method.name} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded-md ${styling.badge}`}>
                              {getPaymentMethodIcon(method.name)}
                            </div>
                            <span className="font-semibold text-slate-200">
                              {method.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-400 font-medium">
                              R${" "}
                              {method.amount.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                            <span
                              className={`text-xs font-bold px-2 py-0.5 rounded-full border ${styling.badge} ${styling.text}`}
                            >
                              {method.percent.toFixed(1)}%
                            </span>
                          </div>
                        </div>

                        <div className="h-2 w-full bg-slate-900 rounded-full overflow-hidden border border-slate-800/60">
                          <div
                            className={`h-full ${styling.bg} rounded-full transition-all duration-500 ease-out`}
                            style={{ width: `${Math.max(method.percent, 0)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-slate-950 border border-slate-800/80 rounded-xl p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800/60 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-500/10 text-amber-400 rounded-lg">
                    <Sparkles size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-100">
                      Serviços Mais Rentáveis
                    </h3>
                    <p className="text-xs text-slate-400">
                      Top 5 serviços com maior faturamento no período
                    </p>
                  </div>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400">
                  Top 5
                </span>
              </div>

              {metrics.topServices.length === 0 ? (
                <div className="p-8 text-center border border-slate-800/60 border-dashed rounded-xl">
                  <Layers className="mx-auto text-slate-600 mb-2" size={32} />
                  <p className="text-sm font-medium text-slate-400">
                    Nenhum serviço computado
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Ordens de serviço finalizadas aparecerão aqui.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {metrics.topServices.map((service, index) => {
                    const rank = index + 1;
                    let rankBadge =
                      "bg-slate-800 text-slate-400 border-slate-700";
                    if (rank === 1) {
                      rankBadge =
                        "bg-amber-500/20 text-amber-400 border-amber-500/40 font-extrabold";
                    } else if (rank === 2) {
                      rankBadge =
                        "bg-slate-300/20 text-slate-200 border-slate-400/40 font-bold";
                    } else if (rank === 3) {
                      rankBadge =
                        "bg-amber-700/20 text-amber-500 border-amber-600/40 font-bold";
                    }

                    const barPercent =
                      (service.totalRevenue / metrics.topServiceMaxRevenue) *
                      100;

                    return (
                      <div
                        key={service.name}
                        className="p-3 bg-slate-900/60 hover:bg-slate-900 border border-slate-800/60 hover:border-slate-700/80 rounded-xl transition-all duration-200 ease-out space-y-2"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs border shrink-0 ${rankBadge}`}
                            >
                              #{rank}
                            </span>
                            <span className="font-semibold text-sm text-slate-200 truncate">
                              {service.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-slate-400">
                              {service.count}{" "}
                              {service.count === 1 ? "execução" : "execuções"}
                            </span>
                            <span className="text-sm font-bold text-emerald-400">
                              R${" "}
                              {service.totalRevenue.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>

                        <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden border border-slate-800/40">
                          <div
                            className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${Math.max(barPercent, 2)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-r from-slate-950 via-slate-950 to-indigo-950/20 border border-slate-800/80 hover:border-slate-700/80 rounded-xl p-5 shadow-sm transition-all duration-200 ease-out">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-3.5">
                <div className="p-3 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-xl">
                  <Users size={22} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-100 flex items-center gap-2">
                    Eficiência e Comissões da Equipe
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Total de comissões geradas/pagas aos colaboradores no
                    período selecionado.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 self-end md:self-auto">
                <div className="text-right">
                  <p className="text-xs text-slate-500 font-medium">
                    Total de Comissões
                  </p>
                  <h4 className="text-xl font-extrabold text-indigo-400 tracking-tight">
                    R${" "}
                    {metrics.commissionsTotal.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </h4>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold text-center">
                  <span>{metrics.commissionPercent.toFixed(1)}%</span>
                  <span className="block text-[10px] text-slate-400 font-normal">
                    da receita bruta
                  </span>
                </div>
              </div>
            </div>

            {metrics.receitas > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-800/60">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                  <span>Impacto das Comissões no Faturamento Bruto</span>
                  <span>{metrics.commissionPercent.toFixed(1)}% do total</span>
                </div>
                <div className="h-2 w-full bg-slate-900 rounded-full overflow-hidden border border-slate-800/60">
                  <div
                    className="h-full bg-indigo-500 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${Math.min(Math.max(metrics.commissionPercent, 0), 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
